// src/components/ui/cards/ServiceListItem/ServiceListItemShadcn.jsx
import React from 'react';
import {
  DollarSign,
  Clock,
  Package,
  Trash2
} from 'lucide-react';

// shadcn components
import { Card, CardContent } from '../../card';
import { Button } from '../../button';
import { Badge } from '../../badge';

import { cn } from '../../../../utils/cn';

const ServiceListItemShadcn = ({
  service,
  isSelected = false,
  onClick,
  onDelete
}) => {
  const formatPrice = (price) => {
    if (!price && price !== 0) return 'Not set';
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'Not set';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const componentCount = service.components?.length || 0;

  return (
    <Card
      onClick={onClick}
      className={cn(
        "relative transition-all duration-200 cursor-pointer group",
        "hover:scale-[1.02] hover:shadow-lg",
        isSelected
          ? "bg-primary/10 border-primary/50 shadow-lg shadow-primary/20"
          : "hover:bg-accent/50"
      )}
    >
      <CardContent className="p-4">
        {/* Selected indicator */}
        {isSelected && (
          <div className="absolute left-0 top-4 bottom-4 w-1 bg-gradient-to-b from-primary to-primary/60 rounded-r" />
        )}

        {/* Service Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {/* Color Indicator */}
              <div
                className="w-3 h-3 rounded-full border border-border"
                style={{ backgroundColor: service.color_code || '#8B5CF6' }}
              />

              {/* Service Name */}
              <h3 className={cn(
                "font-semibold truncate transition-colors",
                isSelected ? "text-primary" : "text-foreground group-hover:text-foreground"
              )}>
                {service.name}
              </h3>

              {/* Active Status */}
              <div className={cn(
                "w-2 h-2 rounded-full",
                service.active ? "bg-emerald-400" : "bg-muted-foreground"
              )} />
            </div>

            {/* Description Preview */}
            {service.description && (
              <p className="text-muted-foreground text-xs line-clamp-1 mb-2">
                {service.description}
              </p>
            )}
          </div>

          {/* Actions Menu */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="h-8 w-8 p-0 text-muted-foreground hover:text-red-400 hover:bg-red-500/20"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Service Details */}
        <div className="space-y-2">
          {/* Price */}
          <div className="flex items-center gap-2 text-xs">
            <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
            <span className={cn(
              "font-medium",
              isSelected ? "text-foreground" : "text-muted-foreground"
            )}>
              {formatPrice(service.base_price)}
            </span>
          </div>

          {/* Duration */}
          <div className="flex items-center gap-2 text-xs">
            <Clock className="w-3.5 h-3.5 text-blue-400" />
            <span className={cn(
              "font-medium",
              isSelected ? "text-foreground" : "text-muted-foreground"
            )}>
              {formatDuration(service.base_duration)}
            </span>
          </div>

          {/* Components */}
          {componentCount > 0 && (
            <div className="flex items-center gap-2 text-xs">
              <Package className="w-3.5 h-3.5 text-purple-400" />
              <span className={cn(
                "font-medium",
                isSelected ? "text-primary" : "text-muted-foreground"
              )}>
                {componentCount} component{componentCount !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* Badges */}
        <div className="mt-3 flex flex-wrap gap-2">
          {/* Pricing Configuration Badge */}
          {service.pricing_display_type && service.pricing_display_type !== 'none' && (
            <Badge variant="secondary" className="text-xs bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
              Pricing configured
            </Badge>
          )}

          {/* Status Badge */}
          {!service.active && (
            <Badge variant="outline" className="text-xs bg-muted/20 text-muted-foreground border-muted-foreground/30">
              Inactive
            </Badge>
          )}

          {/* Requires Confirmation Badge */}
          {service.requires_confirmation && (
            <Badge variant="outline" className="text-xs bg-amber-500/20 text-amber-400 border-amber-500/30">
              Requires confirmation
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ServiceListItemShadcn;